import { useEffect, useState } from "react";
import { ShieldCheck, ExternalLink } from "lucide-react";
import api from "@/lib/api";


export default function FCRAAckModal() {
  const [show, setShow] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Skip the network round-trip if we have a local cache of the ack.
    if (typeof window !== "undefined" && window.localStorage.getItem("fcra_acked_v1") === "1") {
      return;
    }
    let cancelled = false;
    api.get("/compliance/fcra-ack")
      .then((r) => {
        if (cancelled) return;
        if (r.data?.acknowledged) {
          try { window.localStorage.setItem("fcra_acked_v1", "1"); } catch (e) { /* ignore */ }
        } else {
          setShow(true);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!show) return null;

  const submit = async () => {
    if (!accepted) return;
    setSubmitting(true);
    try {
      await api.post("/compliance/fcra-ack", { accept: true });
      try { window.localStorage.setItem("fcra_acked_v1", "1"); } catch (e) { /* ignore */ }
      setShow(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="fcra-ack-modal">
      <div className="bg-[#0A0D12] border border-white/10 rounded-sm max-w-2xl w-full p-6 space-y-5 shadow-2xl">
        <div className="flex items-start gap-3">
          <ShieldCheck className="h-6 w-6 text-emerald-400 mt-1 shrink-0" />
          <div>
            <h2 className="font-heading text-xl">Terms of Use Acknowledgment</h2>
            <p className="text-xs text-slate-500 mt-1">One-time acknowledgment required to access lead-discovery features.</p>
          </div>
        </div>

        <div className="text-sm text-slate-300 space-y-3 leading-relaxed">
          <p>
            <strong className="text-slate-100">This platform is for real estate and business acquisition research only.</strong>
            {" "}Use of data for FCRA-regulated purposes is prohibited. Specifically, you agree NOT to use any data accessed
            through Acquigine for:
          </p>
          <ul className="list-disc list-inside space-y-1 text-slate-400 text-xs ml-2">
            <li>Employment screening or background checks for hiring decisions</li>
            <li>Tenant screening or rental application decisions</li>
            <li>Credit decisions, loan underwriting, or consumer credit reporting</li>
            <li>Insurance underwriting or eligibility decisions</li>
            <li>Any other purpose regulated by the Fair Credit Reporting Act (FCRA)</li>
          </ul>
          <p className="text-xs text-slate-400">
            You also acknowledge our <strong className="text-slate-200">Fair Housing</strong> commitment: lead-scoring outputs do not
            use race, ethnicity, religion, national origin, sex, disability, or familial status (directly or as proxies).
            Outreach to owners identified via divorce, probate, or eviction signals must use neutral language - the underlying
            life event must never be referenced.
          </p>
          <p className="text-xs text-slate-500">
            By accepting, you agree to use this platform in compliance with all applicable federal, state, and local laws,
            including the Fair Housing Act, FCRA, TCPA, and CAN-SPAM Act.
          </p>
        </div>

        <label className="flex items-start gap-2 text-sm text-slate-200 cursor-pointer" data-testid="fcra-accept-label">
          <input
            type="checkbox"
            data-testid="fcra-accept-checkbox"
            checked={accepted}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-1 accent-emerald-500"
          />
          <span>
            I have read and agree to the Acquigine Terms of Use, FCRA prohibition, Fair Housing commitment, and the
            ethical outreach policy regarding life-event signals.
          </span>
        </label>

        <div className="flex items-center justify-between">
          <a href="/compliance" target="_blank" rel="noopener noreferrer"
             className="text-xs text-emerald-400 hover:underline inline-flex items-center gap-1">
            <ExternalLink className="h-3 w-3" /> Full legal disclosures
          </a>
          <button
            data-testid="fcra-accept-submit"
            disabled={!accepted || submitting}
            onClick={submit}
            className="btn-primary text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? "Recording..." : "I Accept & Continue"}
          </button>
        </div>
      </div>
    </div>
  );
}
